import React, {useMemo, useState} from 'react';

import {format} from 'date-fns';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {useRouteMatch} from 'react-router-dom';

import {ReactComponent as DownloadIcon} from 'assets/svg/download2.svg';

import {ReactComponent as UploadIcon} from 'assets/svg/icon_menu.svg';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {ContentContainer, StatusWrapper} from 'components/ui-kit/PageComponents';
import {STATUS_COLOR_LIST} from 'core/constants/statuses';
import {ROUTE} from 'core/routes';
import {downloadInvoiceFx, setInvoiceStatusChangeModalHandler} from 'models/profile/billingIvoice';
import {colors} from 'theme';
import {UserRoles} from 'types';
import {
    BillingHistoryAccessors as Accessors,
    BillingInvoiceRawRow,
    BillingInvoicesAccessors,
    BillingInvoiceStatus,
    BillingRawRow,
    BillingType,
} from 'types/billing';
import {handleNil} from 'utils/handleNil';

import {ProfileNavigationTabs} from '../NavigationTabs';
import {Wrapper} from '../styled';

import {GridsNavigation} from './BillingNavigation';
import {BillingHeaderControl} from './HeaderControl';
import {useSetTableConfig} from './Hooks/useSetTableConfig';
import {InvoiceGeneratingModal} from './Modals/InvoiceGeneratingModal';
import {InvoiceStatusChangeModal} from './Modals/InvoiceStatusChangeModal';
import {HeaderControlContainer} from './styled';
import {BillingGridTable} from './Table';
import {BillingInvoicesGridTable} from './TableInvoces';

const downloadInvoice = async (id: string) => {
    try {
        const response = await downloadInvoiceFx(id);

        const url = URL.createObjectURL(response.data);

        const contentDisposition =
            response.headers['content-disposition'] || response.headers['Content-Disposition'];

        const raw = contentDisposition?.split('filename=')[1]?.trim() ?? '';
        const filename = raw.replace(/^"|"$/g, '') || `invoice-${Date.now()}.pdf`;

        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `invoice-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error('Error downloading invoice:', error);
    }
};

const formatIssueDateTime = (issueDate: string, issueTime?: string) => {
    if (!issueTime) return format(new Date(issueDate), 'dd.MM.yyyy');

    const timeZoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(issueTime) ? '' : 'Z';
    const date = new Date(`${issueDate.slice(0, 10)}T${issueTime}${timeZoneSuffix}`);

    return Number.isNaN(date.getTime())
        ? `${format(new Date(issueDate), 'dd.MM.yyyy')} ${issueTime}`
        : format(date, 'dd.MM.yyyy HH:mm');
};

export function convertData(
    data: BillingRawRow[] | undefined,
    t: TFunction,
    downloadPending: boolean,
): Partial<BillingRawRow>[] {
    if (!data) {
        return [];
    }

    function getStatusColors(type?: BillingType) {
        const map: Record<BillingType | 'DEFAULT', string> = {
            CREDIT: STATUS_COLOR_LIST['RED'],
            DEBIT: STATUS_COLOR_LIST['GREEN'],
            DEFAULT: STATUS_COLOR_LIST['GRAY'],
        };
        return {color: map[type ?? 'DEFAULT']};
    }

    return data.map((elem) => ({
        [Accessors.id]: handleNil(elem.id),
        [Accessors.documentNumber]: handleNil(elem.documentNumber),
        [Accessors.amount]: handleNil(elem.amount),
        [Accessors.operationDate]: format(new Date(elem.operationDate), 'dd.MM.yyyy'),
        [Accessors.transactionType]: handleNil(elem.transactionType, (value) => (
            <StatusWrapper {...getStatusColors(elem.transactionType)}>
                {t(`dictionaries.billingStatus.${value}`)}
            </StatusWrapper>
        )),
        [Accessors.action]: handleNil(elem, (value) => {
            if (value.transactionType === 'DEBIT') {
                return (
                    <IconButton
                        disabled={downloadPending}
                        icon={
                            <Icon
                                source={DownloadIcon}
                                width={16}
                                height={16}
                                strokeColor={colors.brandPrimary}
                                fillColor={'transparent'}
                            />
                        }
                        type="button"
                        hoverIndication={true}
                        onClick={() => {
                            downloadInvoice(value.documentNumber);
                        }}
                    />
                );
            }
            return null;
        }),
    }));
}

export function convertDataInvoices(
    data: BillingInvoiceRawRow[] | undefined,
    t: TFunction,
): Partial<BillingInvoiceRawRow>[] {
    if (!data) {
        return [];
    }

    function getStatusColors(type?: BillingInvoiceStatus) {
        const map: Record<BillingInvoiceStatus | 'DEFAULT', string> = {
            SENT: STATUS_COLOR_LIST['RED'],
            PAID: STATUS_COLOR_LIST['GREEN'],
            PARTIALLY_PAID: STATUS_COLOR_LIST['GREEN_ALT'],
            CANCELLED: STATUS_COLOR_LIST['ORANGE'],
            CLOSED: STATUS_COLOR_LIST['TEAL'],
            WRITTEN_OFF: STATUS_COLOR_LIST['GRAY_ALT'],
            OVERDUE: STATUS_COLOR_LIST['BLUE'],
            DEFAULT: STATUS_COLOR_LIST['GRAY'],
        };
        return {color: map[type ?? 'DEFAULT']};
    }

    return data.map((elem) => ({
        [BillingInvoicesAccessors.irn]: handleNil(elem.irn),
        [BillingInvoicesAccessors.tin]: handleNil(elem.client?.tin),
        [BillingInvoicesAccessors.issueDate]: formatIssueDateTime(elem.issueDate, elem.issueTime),
        [BillingInvoicesAccessors.totalInvoicedQuantity]: handleNil(elem.totalInvoicedQuantity),
        [BillingInvoicesAccessors.payableAmount]: handleNil(elem.payableAmount),
        [BillingInvoicesAccessors.status]: handleNil(elem.status, () => (
            <>
                <StatusWrapper {...getStatusColors(elem.status)}>
                    {t(`dictionaries.billingInvoiceStatus.${elem.status}`)}
                </StatusWrapper>{' '}
                {elem.status === 'SENT' && !!elem.client && (
                    <IconButton
                        icon={
                            <Icon
                                source={UploadIcon}
                                width={24}
                                height={24}
                                fillColor={colors.darkGrey}
                            />
                        }
                        type="button"
                        hoverIndication={true}
                        onClick={() => {
                            setInvoiceStatusChangeModalHandler({
                                show: true,
                                data: {
                                    docNumber: elem.id,
                                    irn: elem.irn,
                                    email: elem.client?.email ?? '',
                                    tin: elem.client?.tin ?? '',
                                },
                            });
                        }}
                    />
                )}
            </>
        )),
    }));
}

const HeaderControl = ({
    setShowInvoiceGeneratingModal,
    isSearchable,
}: {
    setShowInvoiceGeneratingModal: (show: boolean) => void;
    isSearchable?: boolean;
}) => (
    <HeaderControlContainer>
        <BillingHeaderControl
            setShowInvoiceGeneratingModal={setShowInvoiceGeneratingModal}
            isSearchable={isSearchable}
        />
        <PermissionAccess permissions={[]} roleAccess={[UserRoles.ROLE_MPL_OPERATOR_ADMIN]}>
            <GridsNavigation />
        </PermissionAccess>
    </HeaderControlContainer>
);

export const BillingHistory = () => {
    const {t} = useTranslation();
    const {path} = useRouteMatch();
    const [showInvoiceGeneratingModal, setShowInvoiceGeneratingModal] = useState(false);

    const isBillingPage = useMemo(() => path === ROUTE.BILLING, [path]);

    const setTableConfig = useSetTableConfig({path: path as string});

    return (
        <Page title={t('pages.profile.title')}>
            <InvoiceGeneratingModal
                show={showInvoiceGeneratingModal}
                onClose={() => {
                    setShowInvoiceGeneratingModal(false);
                }}
            />
            <InvoiceStatusChangeModal />
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    {isBillingPage ? (
                        <BillingGridTable
                            headerControls={
                                <HeaderControl
                                    setShowInvoiceGeneratingModal={setShowInvoiceGeneratingModal}
                                />
                            }
                            configFunc={setTableConfig}
                            dataMapper={convertData}
                        />
                    ) : (
                        <BillingInvoicesGridTable
                            headerControls={
                                <HeaderControl
                                    setShowInvoiceGeneratingModal={setShowInvoiceGeneratingModal}
                                    isSearchable={!isBillingPage}
                                />
                            }
                            configFunc={setTableConfig}
                            dataMapper={convertDataInvoices}
                        />
                    )}
                </ContentContainer>
            </Wrapper>
        </Page>
    );
};
